import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '@/environments/environment';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
  BriefResponseDto,
  NewBriefShoutDto,
  SocialChannelEnum,
} from '@/app/common/general-types/briefs.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { AdminCreateSubmittedPostBody, SubmittedPostDto } from '@/app/common/general-types/submitted-posts.dto';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { UserResponseDto } from '@/app/services/auth/auth.types';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import {
  BriefTrackerCandidatePost,
  BriefTrackerLookupResult,
  BriefTrackerStatusUser,
  BriefTrackerSuperSocialProfile,
} from '../molecules/brief-tracker/brief-tracker.types';
import {
  attachStatusHistoryUsers,
  briefTypeFromBrief,
  briefVisibilityLabel,
  buildSuperSocialProfile,
  buildVideoChecklist,
  creatorFullName,
  emptySuperSocialProfile,
  formatBriefTrackerDate,
  sortStatusHistoryAsc,
  uniqueStatusHistoryUserIds,
} from '../molecules/brief-tracker/brief-tracker.helpers';

@Injectable({
  providedIn: 'root',
})
export class AdminBriefTrackerService {
  private http = inject(HttpClient);
  private superSocialService = inject(SuperSocialService);
  private readonly api = environment.shout_api.url;

  runCompletionCheck(applicationId: string): Observable<unknown> {
    const url = `${this.api}/scheduled-tasks/check-brief-complete`;
    return this.http.post<unknown>(url, { applicationId });
  }

  /** Queue validation (GCP in deployed envs). */
  validateSubmittedPost(submittedPostId: string): Observable<unknown> {
    const url = `${this.api}/submitted-posts/${submittedPostId}/validate`;
    return this.http.post<unknown>(url, {});
  }

  /** Run SuperSocial validation immediately (needed when API runs locally without GCP tasks). */
  runDeferredSuperSocialValidation(submittedPostId: string): Observable<unknown> {
    const url = `${this.api}/scheduled-tasks/deferred/submitted-post-super-social`;
    return this.http.post<unknown>(url, { submittedPostId });
  }

  validateSubmittedPostForTracker(submittedPostId: string): Observable<unknown> {
    const usesLocalApi = environment.shout_api.url.includes('localhost');
    return this.validateSubmittedPost(submittedPostId).pipe(
      switchMap(() =>
        usesLocalApi ? this.runDeferredSuperSocialValidation(submittedPostId) : of(null),
      ),
    );
  }

  forceValidateSubmittedPost(submittedPostId: string): Observable<unknown> {
    const url = `${this.api}/submitted-posts/${submittedPostId}/force-validate`;
    return this.http.post<unknown>(url, {});
  }

  forceValidateSubmittedPostForTracker(
    submittedPostId: string,
    applicationId: string,
    applicationStatus?: number,
  ): Observable<unknown> {
    return this.forceValidateSubmittedPost(submittedPostId).pipe(
      switchMap(() =>
        applicationStatus === ApplicationStatusEnum.ReadyToPost
          ? this.runCompletionCheck(applicationId)
          : of(null),
      ),
    );
  }

  updateSubmittedPostForTracker(
    submittedPostId: string,
    patch: Pick<SubmittedPostDto, 'fullUrl' | 'videoLink' | 'adCode'>,
  ): Observable<SubmittedPostDto> {
    const url = `${this.api}/submitted-posts/${submittedPostId}`;
    return this.http.patch<SubmittedPostDto>(url, patch);
  }

  resolveVideoLinkForTracker(
    videoLink: string,
    socialChannel: number,
  ): Observable<{ fullUrl: string; linkHandle?: string | null; extractedVideoId?: string | null }> {
    const url = `${this.api}/submitted-posts/resolve-video-link`;
    return this.http.post<{ fullUrl: string; linkHandle?: string | null; extractedVideoId?: string | null }>(
      url,
      { videoLink, socialChannel },
    );
  }

  createSubmittedPostForTracker(
    body: AdminCreateSubmittedPostBody,
  ): Observable<SubmittedPostDto> {
    const url = `${this.api}/submitted-posts/admin/from-link`;
    return this.http.post<SubmittedPostDto>(url, body);
  }

  loadCandidatePosts(
    socialChannel: number,
    profile: BriefTrackerSuperSocialProfile,
  ): Observable<BriefTrackerCandidatePost[]> {
    if (socialChannel === SocialChannelEnum['TikTok Post']) {
      const account = profile.accounts.find((row) => row.platform === 'tiktok');
      if (!account) {
        return of([]);
      }
      return this.superSocialService.getPostsForSelectionTikTok(account.id).pipe(
        map((response) =>
          (response.videos ?? [])
            .map((video) => this.toCandidatePost({
              id: video.id,
              title: video.title,
              shareUrl: video.share_url,
              thumbnailUrl: video.cover_image_url,
              postedAt: this.unixSecondsToIso(video.create_time),
            }))
            .filter((post): post is BriefTrackerCandidatePost => post != null),
        ),
        catchError(() => of([])),
      );
    }

    if (
      socialChannel === SocialChannelEnum['Instagram Reel'] ||
      socialChannel === SocialChannelEnum['Instagram Story']
    ) {
      const account = profile.accounts.find((row) => row.platform === 'instagram');
      if (!account) {
        return of([]);
      }
      return this.superSocialService.getPostsForSelectionInstagram(account.id).pipe(
        map((response) =>
          (response.data ?? [])
            .map((post) => this.toCandidatePost({
              id: post.post_id ?? post.share_url ?? '',
              title: post.title,
              shareUrl: post.share_url,
              thumbnailUrl: post.thumbnail_url,
              postedAt: post.timestamp ?? null,
            }))
            .filter((post): post is BriefTrackerCandidatePost => post != null),
        ),
        catchError(() => of([])),
      );
    }

    return of([]);
  }

  private toCandidatePost(raw: {
    id: string;
    title?: string;
    shareUrl?: string;
    thumbnailUrl?: string;
    postedAt: string | null;
  }): BriefTrackerCandidatePost | null {
    const shareUrl = raw.shareUrl?.trim() ?? '';
    const id = raw.id?.trim() ?? '';
    if (!shareUrl || !id) {
      return null;
    }
    return {
      id,
      title: raw.title?.trim() || 'Untitled post',
      shareUrl,
      thumbnailUrl: raw.thumbnailUrl?.trim() || null,
      postedAtLabel: raw.postedAt ? formatBriefTrackerDate(raw.postedAt) : null,
    };
  }

  private unixSecondsToIso(createTime: number | undefined): string | null {
    if (createTime == null || !Number.isFinite(createTime)) {
      return null;
    }
    return new Date(createTime * 1000).toISOString();
  }

  getSubmittedPostsForApplication(briefApplicationId: string): Observable<SubmittedPostDto[]> {
    return this.getSubmittedPostsForBriefApplication(briefApplicationId);
  }

  /** Move HiredRemoved (13) back to Applied (2) so the brand can hire again. */
  restoreUnhiredToApplied(
    briefApplicationId: string,
  ): Observable<BriefApplicationResponseDto> {
    const url = `${this.api}/brief-application/admin/restore-unhired-to-applied/${briefApplicationId}`;
    return this.http.post<BriefApplicationResponseDto>(url, {});
  }

  /** Un-hire via admin set-status -> HiredRemoved (13). Releases wallet reservation. */
  unhireApplicationForTracker(
    briefApplicationId: string,
    userId: string,
    reason = 'Admin un-hire via Brief Tracker',
  ): Observable<BriefApplicationResponseDto> {
    const url = `${this.api}/brief-application/admin/set-application-status/${briefApplicationId}`;
    return this.http.post<BriefApplicationResponseDto>(url, {
      status: ApplicationStatusEnum.HiredRemoved,
      user: userId,
      reason,
    });
  }

  lookup(briefId: string, creatorId: string): Observable<BriefTrackerLookupResult> {
    return forkJoin({
      creator: this.getCreator(creatorId),
      brief: this.getBrief(briefId),
      applicationWithBrief: this.getBriefApplicationWithBrief(briefId, creatorId),
    }).pipe(
      switchMap(({ creator, brief, applicationWithBrief }) => {
        const application = applicationWithBrief;
        const resolvedBrief =
          brief ?? (application?.brief as BriefResponseDto | undefined) ?? null;
        const shoutBriefId =
          resolvedBrief?.shoutBriefId ?? resolvedBrief?.shoutBrief?.id ?? null;

        const shoutBrief$ =
          resolvedBrief?.shoutBrief != null
            ? of(resolvedBrief.shoutBrief)
            : shoutBriefId
              ? this.getBriefShout(shoutBriefId)
              : of(null);

        const submittedPosts$ = application?.id
          ? this.getSubmittedPostsForBriefApplication(application.id)
          : of<SubmittedPostDto[]>([]);
        const videos$ = this.getCreatorBriefVideos(creatorId, briefId);
        const superSocial$ = this.getSuperSocialCreator(creatorId);

        return forkJoin({
          shoutBrief: shoutBrief$,
          submittedPosts: submittedPosts$,
          videos: videos$,
          superSocial: superSocial$,
        }).pipe(
          map(({ shoutBrief, submittedPosts, videos, superSocial }) =>
            this.buildResult(
              briefId,
              creatorId,
              creator,
              resolvedBrief,
              application,
              submittedPosts,
              shoutBrief,
              videos,
              superSocial,
            ),
          ),
          switchMap((result) => this.withStatusHistoryUsers(result)),
        );
      }),
    );
  }

  private buildResult(
    briefId: string,
    creatorId: string,
    creator: CreatorResponseDto | null,
    brief: BriefResponseDto | null,
    application: BriefApplicationResponseDto | null,
    submittedPosts: SubmittedPostDto[],
    shoutBrief: NewBriefShoutDto | null = null,
    videos: VideoResponseDto[] = [],
    superSocial = emptySuperSocialProfile(),
  ): BriefTrackerLookupResult {
    const { label, detail } = briefTypeFromBrief(brief);
    const resolvedShoutBrief = shoutBrief ?? brief?.shoutBrief ?? null;

    return {
      briefId,
      creatorId,
      creator,
      creatorFullName: creatorFullName(creator),
      brief,
      briefTitle: brief?.title ?? '—',
      briefCreatedDate: brief?.createdDate ? formatBriefTrackerDate(brief.createdDate) : null,
      briefVisibility: briefVisibilityLabel(brief),
      briefType: label,
      briefTypeDetail: detail,
      application,
      statusHistory: sortStatusHistoryAsc(application?.statusHistory ?? []),
      shoutBrief: resolvedShoutBrief,
      submittedPosts,
      submittedPostsFound: submittedPosts.length > 0,
      videoChecklist: buildVideoChecklist(brief, videos, application),
      superSocial,
    };
  }

  private withStatusHistoryUsers(
    result: BriefTrackerLookupResult,
  ): Observable<BriefTrackerLookupResult> {
    const userIds = uniqueStatusHistoryUserIds(result.statusHistory);
    if (userIds.length === 0) {
      return of(result);
    }

    return forkJoin(
      userIds.map((userId) =>
        this.getStatusUser(userId).pipe(map((user) => ({ userId, user }))),
      ),
    ).pipe(
      map((rows) => {
        const usersById: Record<string, BriefTrackerStatusUser | null> = {};
        for (const row of rows) {
          usersById[row.userId] = row.user;
        }
        return {
          ...result,
          statusHistory: attachStatusHistoryUsers(result.statusHistory, usersById),
        };
      }),
    );
  }

  private getStatusUser(userId: string): Observable<BriefTrackerStatusUser | null> {
    return this.http.get<UserResponseDto>(`${this.api}/users/${userId}`).pipe(
      map((user) =>
        user
          ? {
              firstName: user.firstName ?? null,
              lastName: user.lastName ?? null,
              email: user.email ?? null,
            }
          : null,
      ),
      catchError(() => of(null)),
    );
  }

  getSuperSocialProfile(creatorId: string) {
    return this.getSuperSocialCreator(creatorId);
  }

  private getSuperSocialCreator(creatorId: string) {
    return this.superSocialService.getCreatorByExternalIdApp(creatorId).pipe(
      map((raw) => buildSuperSocialProfile(raw)),
      catchError(() => of(emptySuperSocialProfile())),
    );
  }

  private getCreatorBriefVideos(
    creatorId: string,
    briefId: string,
  ): Observable<VideoResponseDto[]> {
    const url = `${this.api}/videos/creator-brief-videos/modern/${creatorId}/${briefId}`;
    return this.http.get<VideoResponseDto[]>(url).pipe(
      map((videos) => videos ?? []),
      catchError(() => of([])),
    );
  }

  private getCreator(creatorId: string): Observable<CreatorResponseDto | null> {
    return this.http.get<CreatorResponseDto>(`${this.api}/creators/${creatorId}`).pipe(
      map((creator) => creator ?? null),
      catchError(() => of(null)),
    );
  }

  private getBrief(briefId: string): Observable<BriefResponseDto | null> {
    return this.http.get<BriefResponseDto>(`${this.api}/briefs/${briefId}`).pipe(
      map((brief) => brief ?? null),
      catchError(() => of(null)),
    );
  }

  private getBriefApplicationWithBrief(
    briefId: string,
    creatorId: string,
  ): Observable<BriefApplicationResponseDto | null> {
    const url = `${this.api}/brief-application/student-application-with-brief/${briefId}/${creatorId}`;
    return this.http.get<BriefApplicationResponseDto | null>(url).pipe(catchError(() => of(null)));
  }

  private getBriefShout(shoutBriefId: string): Observable<NewBriefShoutDto | null> {
    return this.http.get<NewBriefShoutDto>(`${this.api}/brief-shout/${shoutBriefId}`).pipe(
      map((shoutBrief) => shoutBrief ?? null),
      catchError(() => of(null)),
    );
  }

  private getSubmittedPostsForBriefApplication(
    briefApplicationId: string,
  ): Observable<SubmittedPostDto[]> {
    const url = `${this.api}/submitted-posts/brief-application/${briefApplicationId}`;
    return this.http.get<SubmittedPostDto[]>(url).pipe(map((posts) => posts ?? []));
  }
}
